import React from "react";
import { motion } from "framer-motion";
import { Plus, ArrowRight } from "lucide-react";
import { Button } from "./ui/Button";

interface HeroProps {
  onContactClick?: () => void;
}

export const Hero: React.FC<HeroProps> = ({ onContactClick }) => {
  return (
    <div className="relative flex flex-col min-h-screen bg-gray-900"
      style={{ backgroundImage: 'url(/assets/images/hero.webp)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>

      <div className="absolute inset-0 h-full w-full bg-black/30"></div>

      {/* Main Content Area - Adjusted for mobile to fit in 100vh */}
      <div className="relative flex-1 flex flex-col lg:justify-between container mx-auto px-2 lg:px-12 pt-24 lg:pt-[6rem] pb-2 sm:pb-4 lg:pb-6">
        {/* Top Section */}
        <div className="flex flex-col max-md:justify-center lg:flex-row flex-1 min-h-0">
          {/* Main Headline */}
          <div className="relative w-full lg:w-[70%] flex items-center justify-center lg:justify-start mb-4 sm:mb-6 lg:mb-0">
            {/* Divider - Hidden on mobile */}
            <div className="max-lg:hidden absolute right-0 w-[1px] h-full bg-gradient-to-b from-white to-transparent"></div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-5xl text-center lg:text-left space-y-8"
            >
              <div className="flex max-lg:justify-center text-white font-open-sans-hebrew font-bold text-base lg:text-[32px] bg-white/10 backdrop-blur-sm w-fit px-4 rounded-lg">
                Next Generation Plywood
              </div>
              <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-open-sans-hebrew font-bold text-white uppercase leading-tight">
                Built For Modern <br className="hidden sm:block" />
                Construction
              </h1>
              <div className="font-open-sans-hebrew font-bold text-sm lg:text-[32px] text-white flex max-lg:justify-center gap-4 lg:gap-10">
                <span>Solid</span>
                <span>
                  <li>Versatile</li>
                </span>
                <span>
                  <li>Powerful</li>
                </span>
              </div>
            </motion.div>
          </div>

          {/* Right Side Content */}
          <div className="w-full lg:w-[35%] flex flex-col gap-8 max-lg:h-full p-2 sm:p-4 lg:p-8 xl:p-12">
            {/* Top Right Decorative Element - Hidden on mobile/tablet, with top padding to avoid navbar */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="justify-between items-center gap-4 hidden lg:flex w-full"
            >
              {/* <div className="text-white">
                <p className="text-heading-sub font-medium">72-hour BWP test</p>
                <p className="text-body text-white/70 font-light">
                  Boiling-proof grade
                </p>
              </div>
              <div className="flex-1 h-[1px] bg-gradient-to-r from-white to-transparent"></div>
              <div className="text-white animate-spin [animation-duration:10s]">
                <img src="/assets/icons/asterisk.webp" alt="Logo" className="w-10 h-10 stroke-1 inline-block" />
              </div> */}
              <ul className="text-white font-raleway text-[10px] lg:text-[20px] space-y-4">
                <li className="lg:list-disc">CROMA PROTECTOR</li>
                <li className="lg:list-disc">CROMA CLUB PLUS</li>
                <li className="lg:list-disc">CROMA GOLD BWP</li>
                <li className="lg:list-disc">CROMA GOLD MR</li>
                <li className="lg:list-disc">CROMA ENGINEERED (BWP)</li>
                <li className="lg:list-disc">CROMA ENGINEERED (MR)</li>
              </ul>
            </motion.div>

            {/* Floating Button - Desktop */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="hidden lg:flex items-center"
            >
              <Button variant="red" onClick={onContactClick} className="font-raleway">
                Let's Discuss
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Mobile Button - Positioned between sections */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="flex lg:hidden justify-center items-center my-2 sm:my-4"
        >
          <button
            onClick={onContactClick}
            className="bg-white text-black px-6 py-3 h-fit rounded-lg text-sm font-medium font-raleway flex items-center gap-2 group-hover:gap-3 group hover:bg-gray-100 transition-all shadow-lg"
          >
            Let's Discuss
            <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 transition-transform group-hover:translate-x-0.5" />
          </button>
        </motion.div>
      </div>

      {/* Bottom Stats Section - Reduced height on mobile */}
      <div className="max-md:flex-1 h-auto min-h-[140px] sm:min-h-[180px] lg:h-[25%] lg:min-h-[200px] px-2 lg:px-12 z-10 relative pb-4 sm:pb-6 lg:pb-8">
        <div className="absolute bottom-2 sm:bottom-12 lg:bottom-20 left-0 right-0 h-[1px] bg-gradient-to-l from-white to-transparent"></div>
        {/* Bottom Info Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="w-full pt-2 sm:pt-4 lg:pt-6"
        >
          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6 sm:gap-8 lg:gap-10 text-white">
            {/* Right Side Stats */}
            <div className="flex flex-col sm:flex-row lg:grid lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8 lg:gap-12 xl:gap-20 w-full lg:w-auto">
              <div>
                <h4 className="text-heading-sub font-open-sans-hebrew font-bold">35+</h4>
                <p className="text-body text-white/70 mt-1 font-light font-raleway">
                  Years of industry excellence
                </p>
              </div>
              <div>
                <h4 className="text-heading-sub font-open-sans-hebrew font-bold">700–900 kg/m³</h4>
                <p className="text-body text-white/70 mt-1 font-light font-raleway">
                  High-density core strength
                </p>
              </div>
              <div>
                <h4 className="text-heading-sub font-open-sans-hebrew font-bold">72 Hours</h4>
                <p className="text-body text-white/70 mt-1 font-light font-raleway">
                  Certified boiling-water proof durability
                </p>
              </div>

              <div className="flex items-start gap-3 sm:gap-4 max-w-sm h-full lg:col-span-1">
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-white/80 shrink-0 mt-1" />
                <p className="text-body text-white/90 font-light font-raleway">
                  Built with advanced technology for superior strength and
                  long-lasting stability.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};